/**
 * Office Calendar working days drive monthly expected hours and leave accrual.
 * A working day is any calendar day that is not a holiday or weekly off.
 */

export function monthExpectedWorkMinutes(
  monthWorkingDays: number,
  dailyExpectedMinutes: number,
): number {
  if (
    !Number.isFinite(monthWorkingDays) ||
    !Number.isFinite(dailyExpectedMinutes) ||
    monthWorkingDays <= 0 ||
    dailyExpectedMinutes <= 0
  ) {
    return 0;
  }
  return Math.round(monthWorkingDays * dailyExpectedMinutes);
}

export function monthExpectedWorkHours(
  monthWorkingDays: number,
  dailyExpectedMinutes: number,
): number {
  return Math.round(
    (monthExpectedWorkMinutes(monthWorkingDays, dailyExpectedMinutes) / 60) *
      100,
  ) / 100;
}

/** Expected days to work = calendar working days minus monthly leave accrual. */
export function monthExpectedWorkingDays(
  monthWorkingDays: number,
  monthlyLeaveAccrual: number,
): number {
  const workingDays = Number(monthWorkingDays);
  const accrual = Number(monthlyLeaveAccrual);
  if (!Number.isFinite(workingDays) || workingDays <= 0) {
    return 0;
  }
  const leaveDays = Number.isFinite(accrual) && accrual > 0 ? accrual : 0;
  return Math.max(0, Math.round((workingDays - leaveDays) * 100) / 100);
}

export function resolveMonthlyLeaveAccrual(
  policies: Array<{
    isActive?: boolean;
    requiresBalance?: boolean;
    isDefault?: boolean;
    receivesCompensatoryCredit?: boolean | null;
    monthlyAccrual?: number | null;
  }>,
): number {
  const active = policies.filter(
    policy =>
      policy.isActive !== false &&
      policy.requiresBalance !== false &&
      Number(policy.monthlyAccrual) > 0,
  );
  if (!active.length) return 0;
  const preferred =
    active.find(policy => policy.isDefault) ||
    active.find(policy => policy.receivesCompensatoryCredit) ||
    active[0];
  return Math.round(Number(preferred.monthlyAccrual) * 100) / 100;
}

/**
 * Accrual for one month:
 * yearly pool × (working days this month ÷ working days this year).
 * Yearly pool is annual entitlement, or monthlyAccrual × 12 when entitlement is 0.
 */
export function monthLeaveAccrualDays(
  annualEntitlement: number,
  monthWorkingDays: number,
  yearWorkingDays: number,
  monthlyAccrualFallback = 0,
): number {
  const yearPool =
    annualEntitlement > 0 ? annualEntitlement : monthlyAccrualFallback * 12;
  if (
    !Number.isFinite(yearPool) ||
    !Number.isFinite(monthWorkingDays) ||
    !Number.isFinite(yearWorkingDays) ||
    yearPool <= 0 ||
    monthWorkingDays <= 0 ||
    yearWorkingDays <= 0
  ) {
    return 0;
  }
  return Math.round((yearPool * monthWorkingDays * 100) / yearWorkingDays) / 100;
}
